/**
 * Refresh Selected Options Action
 * Refreshes OptLab option entities in the current selection by re-fetching snapshot data
 */

/* global Excel */

import { runExcel } from "../../excel/runExcel";
import { logger } from "../../shared/logger";
import { buildOptionEntityEnrichedFromOccSymbol } from "../../services/options/optionEntityFactory";
import { isOptLabOptionEntityCellValue, getOccSymbolFromEntityCellValue } from "../../shared/excelEntities/optionEntity";
import { createLimiter, sleep, jitter } from "../../shared/async/limitConcurrency";
import { diagnosticsStore, safeErrorMessage, categorizeError } from "../../shared/diagnostics/diagnosticsStore";
import { supportsGetSelectedRanges } from "../../shared/office/supports";

/**
 * Progress callback interface
 */
export type ProgressCallback = (progress: {
  done: number;
  total: number;
  refreshed: number;
  skipped: number;
  failed: number;
  currentSymbol?: string;
}) => void;

/**
 * Work item for refresh operation
 */
interface RefreshWorkItem {
  row: number;
  col: number;
  occSymbol: string;
  rangeIndex: number;
  range: Excel.Range;
}

/**
 * Error record for a failed cell
 */
interface CellError {
  row: number;
  col: number;
  rangeIndex: number;
  message: string;
  address?: string; // Resolved later for failed cells only
}

/**
 * Default concurrency limit
 */
const DEFAULT_CONCURRENCY = 3;

/**
 * Jitter delay range (75-150ms)
 */
const JITTER_BASE = 75;
const JITTER_SPREAD = 75;

/**
 * Get selected ranges, handling both single and multi-area selections
 * @param ctx - Excel RequestContext
 * @returns Array of ranges
 * @throws Friendly error message if selection cannot be retrieved
 */
async function getSelectedRangesOrThrow(ctx: Excel.RequestContext): Promise<Excel.Range[]> {
  // Try multi-range API if supported
  if (supportsGetSelectedRanges(ctx)) {
    try {
      const selectedRanges = (ctx.workbook as any).getSelectedRanges();
      if (!selectedRanges) {
        // Fall through to single-range attempt
      } else {
        // Try standard .items structure first (Office.js standard)
        try {
          selectedRanges.load("items");
          await ctx.sync();
          if (selectedRanges.items && Array.isArray(selectedRanges.items) && selectedRanges.items.length > 0) {
            return selectedRanges.items;
          }
        } catch {
          // If standard .items fails, try .areas.items (alternative structure)
          try {
            if ((selectedRanges as any).areas) {
              (selectedRanges as any).areas.load("items");
              await ctx.sync();
              const itemsProperty = (selectedRanges as any).areas.items;
              if (itemsProperty && Array.isArray(itemsProperty) && itemsProperty.length > 0) {
                return itemsProperty;
              }
            }
          } catch {
            // Both patterns failed - fall through to single-range attempt
          }
        }
        // If items is empty or invalid, fall through to single-range attempt
      }
    } catch (error) {
      // getSelectedRanges() failed - fall through to single-range attempt
      // Don't rethrow here, try single-range instead
    }
  }
  
  // Single-range attempt
  try {
    const range = ctx.workbook.getSelectedRange();
    return [range];
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    
    // Check if this is the known multi-area selection error
    if (
      errorMessage.includes("invalid for this operation") ||
      errorMessage.includes("selection is invalid") ||
      errorMessage.includes("The current selection is invalid") ||
      errorMessage.toLowerCase().includes("invalid for this")
    ) {
      throw new Error(
        "Multiple selections aren't supported in this version of Excel. Please select a single contiguous range."
      );
    }
    
    // Other error - throw friendly message
    throw new Error("Unable to refresh the current selection.");
  }
}

/**
 * Refresh result with errors for UI display
 */
export interface RefreshResult {
  errors: Array<{ address: string; message: string }>;
}

/**
 * Refresh selected option entities
 * @returns Errors array for UI display
 */
export async function refreshSelectedOptionsAction(
  onProgress?: ProgressCallback
): Promise<RefreshResult> {
  const opStartTime = Date.now();
  const opCallId = diagnosticsStore.recordCallStart(
    "OPTIONS.REFRESH_SELECTED",
    "/refresh-selected"
  );

  try {
    return await runExcel(async (context): Promise<RefreshResult> => {
      // Get selected ranges (handles both single and multi-area)
      const ranges = await getSelectedRangesOrThrow(context);

      if (ranges.length === 0) {
        throw new Error("No selection found");
      }

      // Collect all work items from all ranges
      const allWorkItems: RefreshWorkItem[] = [];
      const allErrors: CellError[] = [];
      let totalCells = 0;

      for (let rangeIndex = 0; rangeIndex < ranges.length; rangeIndex++) {
        const range = ranges[rangeIndex];
        range.load(["address", "rowCount", "columnCount", "valuesAsJson"]);
      }

      await context.sync();

      for (let rangeIndex = 0; rangeIndex < ranges.length; rangeIndex++) {
        const range = ranges[rangeIndex];
        const valuesAsJson = range.valuesAsJson as any[][];

        if (!Array.isArray(valuesAsJson)) {
          continue;
        }

        // Iterate 2D array and identify entity cells
        for (let row = 0; row < valuesAsJson.length; row++) {
          const rowData = valuesAsJson[row];
          if (!Array.isArray(rowData)) {
            continue;
          }

          for (let col = 0; col < rowData.length; col++) {
            totalCells++;
            const cellValue = rowData[col];

            if (isOptLabOptionEntityCellValue(cellValue)) {
              const occSymbol = getOccSymbolFromEntityCellValue(cellValue);
              if (occSymbol) {
                allWorkItems.push({
                  row,
                  col,
                  occSymbol,
                  rangeIndex,
                  range,
                });
              } else {
                // Entity detected but no OCC symbol - skip
                allErrors.push({
                  row,
                  col,
                  rangeIndex,
                  message: "Entity found but OCC symbol not extractable",
                });
              }
            }
          }
        }
      }

      const total = allWorkItems.length;
      let done = 0;
      let refreshed = 0;
      let skipped = 0;
      let failed = 0;

      if (total === 0) {
        logger.log("RefreshSelected", "no option entities found", "");
        diagnosticsStore.recordCallEnd(opCallId, {
          ok: true,
          ms: Date.now() - opStartTime,
        });
        return { errors: [] };
      }

      // Create concurrency limiter
      const limiter = createLimiter(DEFAULT_CONCURRENCY);

      // Results: map of work item to updated entity or error
      const results = new Map<
        RefreshWorkItem,
        { entity?: any; error?: string }
      >();

      // Process all work items with throttling
      const promises = allWorkItems.map(async (item) => {
        return limiter.run(async () => {
          // Jitter delay before fetch (inside limiter to spread starts)
          await sleep(jitter(JITTER_BASE, JITTER_SPREAD));

          const symbolCallId = diagnosticsStore.recordCallStart(
            "OPTIONS.SNAPSHOT",
            "/v1/massive/options/snapshot/" + item.occSymbol
          );
          const symbolStartTime = Date.now();

          try {
            // Build enriched entity (this internally calls getOptionSnapshot)
            // The factory handles the fetch and throws UserInputError on failure
            const entity = await buildOptionEntityEnrichedFromOccSymbol(
              item.occSymbol
            );
            results.set(item, { entity });
            refreshed++;

            diagnosticsStore.recordCallEnd(symbolCallId, {
              ok: true,
              ms: Date.now() - symbolStartTime,
            });
          } catch (error) {
            const errorMsg = safeErrorMessage(error);
            results.set(item, { error: errorMsg });
            failed++;

            diagnosticsStore.recordCallEnd(symbolCallId, {
              ok: false,
              ms: Date.now() - symbolStartTime,
            });

            // Determine error category from error type
            let errorCategory: "network" | "auth" | "not_found" | "bad_request" | "backend" | "unknown" = "unknown";
            if (error instanceof Error) {
              // UserInputError from factory indicates snapshot fetch failed
              if (error.message.includes("Market data unavailable")) {
                errorCategory = "backend";
              } else if (error.message.includes("Invalid OCC symbol")) {
                errorCategory = "bad_request";
              }
            }

            diagnosticsStore.recordError({
              category: errorCategory,
              message: errorMsg,
              op_key: "OPTIONS.SNAPSHOT",
              endpoint: "/v1/massive/options/snapshot/" + item.occSymbol,
            });
          } finally {
            done++;
            onProgress?.({
              done,
              total,
              refreshed,
              skipped,
              failed,
              currentSymbol: item.occSymbol,
            });
          }
        });
      });

      // Wait for all to complete
      await Promise.all(promises);

      // Now write back updated entities
      // Group results by range
      const rangeResults = new Map<number, Map<RefreshWorkItem, any>>();
      const rangeErrors = new Map<number, CellError[]>();

      for (const [item, result] of results.entries()) {
        if (result.entity) {
          if (!rangeResults.has(item.rangeIndex)) {
            rangeResults.set(item.rangeIndex, new Map());
          }
          rangeResults.get(item.rangeIndex)!.set(item, result.entity);
        } else if (result.error) {
          if (!rangeErrors.has(item.rangeIndex)) {
            rangeErrors.set(item.rangeIndex, []);
          }
          rangeErrors.get(item.rangeIndex)!.push({
            row: item.row,
            col: item.col,
            rangeIndex: item.rangeIndex,
            message: result.error,
          });
        }
      }

      // Add skipped cells to errors
      for (const error of allErrors) {
        if (!rangeErrors.has(error.rangeIndex)) {
          rangeErrors.set(error.rangeIndex, []);
        }
        rangeErrors.get(error.rangeIndex)!.push(error);
        skipped++;
      }

      // Write back to each range
      for (let rangeIndex = 0; rangeIndex < ranges.length; rangeIndex++) {
        const range = ranges[rangeIndex];
        const rangeItems = rangeResults.get(rangeIndex);
        const rangeItemErrors = rangeErrors.get(rangeIndex) || [];

        if (!rangeItems || rangeItems.size === 0) {
          continue;
        }

        // Build updated valuesAsJson array
        const originalValues = range.valuesAsJson as any[][];
        const updatedValues: any[][] = [];

        for (let row = 0; row < originalValues.length; row++) {
          const originalRow = originalValues[row] || [];
          const updatedRow: any[] = [];

          for (let col = 0; col < originalRow.length; col++) {
            // Find if this cell has an update
            let updated = false;
            for (const [item, entity] of rangeItems.entries()) {
              if (item.row === row && item.col === col) {
                updatedRow.push(entity);
                updated = true;
                break;
              }
            }

            if (!updated) {
              // Keep original value
              updatedRow.push(originalRow[col]);
            }
          }

          updatedValues.push(updatedRow);
        }

        // Try batched write
        try {
          range.valuesAsJson = updatedValues;
          await context.sync();
          logger.log(
            "RefreshSelected",
            "batched write succeeded",
            "range=" + rangeIndex + " cells=" + rangeItems.size
          );
        } catch (error) {
          // Fallback: per-cell write for successfully refreshed items
          logger.log(
            "RefreshSelected",
            "batched write failed, using per-cell fallback",
            safeErrorMessage(error)
          );

          for (const [item, entity] of rangeItems.entries()) {
            try {
              const cell = range.getCell(item.row, item.col);
              cell.valuesAsJson = [[entity]];
              await context.sync();
            } catch (cellError) {
              // Individual cell write failed - add to errors
              rangeItemErrors.push({
                row: item.row,
                col: item.col,
                rangeIndex: item.rangeIndex,
                message: safeErrorMessage(cellError),
              });
              failed++;
            }
          }
        }
      }

      // Resolve addresses for failed cells (small set)
      const finalErrors: Array<{ address: string; message: string }> = [];
      for (const [rangeIndex, errors] of rangeErrors.entries()) {
        const range = ranges[rangeIndex];
        for (const error of errors) {
          try {
            const cell = range.getCell(error.row, error.col);
            cell.load("address");
            await context.sync();
            finalErrors.push({
              address: cell.address,
              message: error.message,
            });
          } catch {
            // If address resolution fails, use row/col
            finalErrors.push({
              address: `Row ${error.row + 1}, Col ${error.col + 1}`,
              message: error.message,
            });
          }
        }
      }

      // Record operation-level diagnostics
      diagnosticsStore.recordCallEnd(opCallId, {
        ok: failed === 0,
        ms: Date.now() - opStartTime,
      });

      logger.log(
        "RefreshSelected",
        "completed",
        "refreshed=" +
          refreshed +
          " failed=" +
          failed +
          " skipped=" +
          skipped
      );

      // Return errors for UI display
      return { errors: finalErrors };
    });
  } catch (error) {
    const errorMessage = safeErrorMessage(error);
    
    // Determine error category
    const isValidationError = 
      errorMessage.includes("Multiple selections aren't supported") ||
      errorMessage.includes("Unable to refresh the current selection");
    
    const errorCategory = isValidationError 
      ? ("validation" as const)
      : categorizeError(error);

    diagnosticsStore.recordCallEnd(opCallId, {
      ok: false,
      ms: Date.now() - opStartTime,
    });

    diagnosticsStore.recordError({
      category: errorCategory,
      message: errorMessage,
      op_key: "OPTIONS.REFRESH_SELECTED",
      endpoint: "/refresh-selected",
    });

    logger.error("RefreshSelected", "action failed", error);
    throw error;
  }
}
